/**
 * <KPICard /> — a tonal metric card with the brand-gradient top
 * strip + an optional 7-day sparkline (Phase 3).
 *
 * Per the Claude Design reference + brand-override.css:
 *   - A 2px brand-gradient strip across the top edge (the brand's
 *     signature horizontal accent — one of the few signature
 *     surfaces, NOT decorative filler).
 *   - A tonal background per category. `indigo` retints to brand
 *     light blue, `teal` to brand teal (brand-override.css lines
 *     227-254); amber / rose / green / purple stay semantic.
 *   - Icon top-left in the tonal color, the number top-right.
 *   - A mini sparkline (Recharts) when a `trend` series is given;
 *     stroke matches the tonal color.
 *   - The whole card is a click-through to its detail page.
 */

import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Line, LineChart, ResponsiveContainer } from "recharts";

export type KPITone =
  | "amber"
  | "indigo"
  | "teal"
  | "rose"
  | "green"
  | "purple"
  | "neutral";

interface ToneStyle {
  card: string;
  icon: string;
  value: string;
  stroke: string;
}

// indigo + teal are the brand retints (brand-override.css);
// amber / rose / green stay semantic; purple is decorative.
const TONES: Record<KPITone, ToneStyle> = {
  indigo: {
    card: "bg-gradient-to-b from-[#e8f0ff] to-[#cfe0ff] border-brand-blue/25",
    icon: "bg-white/60 text-brand-blue",
    value: "text-brand-deep",
    stroke: "#2260c9",
  },
  teal: {
    card: "bg-gradient-to-b from-[#ddf9f6] to-[#b4f0ea] border-brand-teal/30",
    icon: "bg-white/60 text-[#00b5a7]",
    value: "text-[#004b45]",
    stroke: "#00b5a7",
  },
  amber: {
    card: "bg-amber-50 border-amber-200",
    icon: "bg-amber-100 text-amber-700",
    value: "text-amber-800",
    stroke: "#d97706",
  },
  rose: {
    card: "bg-rose-50 border-rose-200",
    icon: "bg-rose-100 text-rose-700",
    value: "text-rose-800",
    stroke: "#e11d48",
  },
  green: {
    card: "bg-emerald-50 border-emerald-200",
    icon: "bg-emerald-100 text-emerald-700",
    value: "text-emerald-800",
    stroke: "#16a34a",
  },
  purple: {
    card: "bg-[#f6ecfa] border-brand-purple/25",
    icon: "bg-white/60 text-brand-purple",
    value: "text-brand-deep",
    stroke: "#a846bf",
  },
  neutral: {
    card: "bg-surface-default border-border-subtle",
    icon: "bg-surface-sunken text-text-secondary",
    value: "text-text-primary",
    stroke: "#7a77a0",
  },
};

export interface KPICardProps {
  tone: KPITone;
  icon: LucideIcon;
  label: string;
  count: number;
  /** Optional breakdown line, e.g. "5 leave · 2 loan · 1 EOS". */
  subtitle?: string | null;
  /** 7-day trend series (oldest → newest) for the sparkline. */
  trend?: number[] | null;
  /** Click-through destination. */
  linkTo: string;
}

function Sparkline({
  data,
  stroke,
}: {
  data: number[];
  stroke: string;
}) {
  const points = data.map((v, i) => ({ i, v }));
  return (
    <div className="mt-auto h-8 w-full pt-1" aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={points}
          margin={{ top: 2, bottom: 2, left: 0, right: 0 }}
        >
          <Line
            type="monotone"
            dataKey="v"
            stroke={stroke}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function KPICard({
  tone,
  icon: Icon,
  label,
  count,
  subtitle,
  trend,
  linkTo,
}: KPICardProps) {
  const styles = TONES[tone] ?? TONES.neutral;
  return (
    <Link
      to={linkTo}
      className={`group relative flex min-h-[132px] flex-col gap-2 overflow-hidden rounded-xl border p-5 transition-shadow duration-micro ease-out hover:shadow-sm ${styles.card}`}
    >
      {/* Signature 2px brand-gradient top strip. */}
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-0.5 bg-brand-gradient-h"
      />
      <header className="flex items-start justify-between">
        <span className={`rounded-md p-2 ${styles.icon}`}>
          <Icon size={18} aria-hidden="true" />
        </span>
        <span
          className={`text-3xl font-bold leading-none tabular-nums ${styles.value}`}
        >
          {count}
        </span>
      </header>
      <div className="flex flex-col gap-0.5">
        <span className="text-base font-semibold text-text-primary">
          {label}
        </span>
        {subtitle && (
          <span className="text-xs text-text-secondary">
            {subtitle}
          </span>
        )}
      </div>
      {trend && trend.length > 1 && (
        <Sparkline data={trend} stroke={styles.stroke} />
      )}
    </Link>
  );
}
